import React, { useCallback, useState } from 'react'
import { connect as connectRedux } from 'react-redux'
import PropTypes from 'prop-types'
import ImmutablePropTypes from 'react-immutable-proptypes'
import { bindActionCreators } from 'redux'
import { compose } from 'recompose'
import { List, Map } from 'immutable'
import _pick from 'lodash/pick'
import _get from 'lodash/get'
import connectHistory from 'gui/components/router/connect'
import Button from 'muicss/lib/react/button'
import Container from 'muicss/lib/react/container'
import Checkbox from 'muicss/lib/react/checkbox'
import Row from 'muicss/lib/react/row'
import Col from 'muicss/lib/react/col'
import { getApplicationDataByKey } from 'services/data'
import { CONTENT_REFERENCE_TYPES } from 'services/content-references/types'
import { parseCSV } from 'services/content/parse'

import {
  CONTENT_BULK_STATE_UNPROCESSED,
  CONTENT_BULK_STATE_PROCESSING,
  CONTENT_BULK_STATE_PROCESSED,
  CONTENT_BULK_APPLICATION_KEY,
  CONTENT_PATH_STORE_KEY,
  CONTENT_BULK_FIELDS,
  CONTENT_STORE_KEY,
} from 'services/content'

import {
  persistData as persistDataAction,
  setDataList as setDataListAction,
  setDataApplicationByKey as setDataApplicationByKeyAction,
  deleteDataApplicationByKey as deleteDataApplicationByKeyAction,
} from 'services/data/redux/actions'

import './index.scss'

// combine all fields, order them with contentId, contentType,
// and language first (in that order), followed by alphabetical order.
const COLUMNS = CONTENT_BULK_FIELDS.union(
  CONTENT_REFERENCE_TYPES,
).sort((a, b) => {
  if (a === 'contentId') return -1
  if (a === 'contentType' && b !== 'contentId') return -1
  if (a === 'language' && b !== 'contentId' && b !== 'contentType') return -1
  if (b === 'contentId' || b === 'contentType' || b === 'language') return 0
  if (a > b) return 1
  if (a < b) return -1
  return 0
})

function Table ({ data, uploadState }) {
  const error = data.get('error', null)
  const source = data.get('source', List())
  const columns = COLUMNS.filter((name) => source.getIn([0, name]) !== undefined).keySeq()
  const colspan = columns.size

  const content = data.get('content', List())
  const status = data.get('status', List())

  if (error) {
    return <p>{error}</p>
  }

  const cols = columns.map((c) => <col data-column={c} key={c} />)
  const headers = columns.map((c) => <th key={c}>{c}</th>)

  const errorRows = status.map((row, index) => {
    const notSuccessful = !row.get('success')
    const errorMessage = row.get('messages', List()).join(', ')
    const rowContent = content.get(index, Map())
    const references = rowContent.get('references', List())
    return notSuccessful ? (
      <React.Fragment key={index}>
        <tr>
          {columns.map((key) => {
            if (!COLUMNS.includes(key)) return null
            if (CONTENT_BULK_FIELDS.includes(key)) {
              return <td key={key}>{rowContent.get(key)}</td>
            }
            if (CONTENT_REFERENCE_TYPES.includes(key)) {
              const refs = references.filter((r) => r.get('type') === key)
              const values = refs.map((r) => r.get('contentId'))
              return (
                <td key={key}>
                  {
                    values.size > 1
                      ? values.join(', ')
                      : values.get(0)
                  }
                </td>
              )
            }
            return <td key={key} />
          }).toList()}
        </tr>
        <tr>
          <td className="content-bulk__message" colSpan={colspan}>
            {errorMessage}
          </td>
        </tr>
      </React.Fragment>
    ) : null
  }).filter((n) => n !== null).toList()

  const parsedRows = source.map((_, index) => {
    const rowStatus = status.get(index, Map())
    const rowContent = content.get(index, Map())
    const references = rowContent.get('references', List())
    const notSuccessful = !rowStatus.get('success')
    return notSuccessful ? null : (
      <tr key={index}>
        {columns.map((key) => {
          if (!COLUMNS.includes(key)) return null
          if (CONTENT_BULK_FIELDS.includes(key)) {
            return <td key={key}>{rowContent.get(key)}</td>
          }
          if (CONTENT_REFERENCE_TYPES.includes(key)) {
            const refs = references.filter((r) => r.get('type') === key)
            const values = refs.map((r) => r.get('contentId'))
            return (
              <td key={key}>
                {
                  values.size > 1
                    ? values.join(', ')
                    : values.get(0)
                }
              </td>
            )
          }
          return <td key={key} />
        }).toList()}
      </tr>
    )
  }).filter((n) => n !== null).toList()

  return (
    <React.Fragment>
      <div className="content-bulk__results content-bulk__results--invalid">
        <table width={`${colspan * 200}px`}>
          <caption>
            <h3>
              {
                uploadState !== CONTENT_BULK_STATE_PROCESSED
                  ? 'Failed to Import'
                  : 'Failed to Save'
              }
            </h3>
            <p>
              {
                uploadState !== CONTENT_BULK_STATE_PROCESSED
                  ? 'The following rows will not be sent to the server.'
                  : 'The following rows could not be saved.'
              }
            </p>
          </caption>
          <colgroup>{cols}</colgroup>
          <thead>
            <tr>{headers}</tr>
          </thead>
          <tbody>
            {
              errorRows.size ? errorRows : (
                <tr>
                  <td colSpan={colspan}>
                    No invalid rows.
                  </td>
                </tr>
              )
            }
          </tbody>
        </table>
      </div>
      <div className="content-bulk__results content-bulk__results--valid">
        <table width={`${colspan * 200}px`}>
          <caption>
            <h3>
              {
                uploadState !== CONTENT_BULK_STATE_PROCESSED
                  ? 'Successfully Imported'
                  : 'Successfully Save'
              }
            </h3>
            <p>
              {
                uploadState !== CONTENT_BULK_STATE_PROCESSED
                  ? 'The following rows will be sent to the server on import.'
                  : 'The following rows were saved successfully.'
              }
            </p>
          </caption>
          <colgroup>{cols}</colgroup>
          <thead>
            <tr>{headers}</tr>
          </thead>
          <tbody>
            {
              parsedRows.size ? parsedRows : (
                <tr>
                  <td colSpan={colspan}>
                    No valid rows.
                  </td>
                </tr>
              )
            }
          </tbody>
        </table>
      </div>
    </React.Fragment>
  )
}

/**
 * A component to import/update content in bulk
 * @param {Object} props The react props
 */
function ContentBulk (props) {
  const {
    data,
    storeKey,
    persistData,
    applicationKey,
    setApplicationState,
    removeApplicationState,
  } = props

  const applicationData = getApplicationDataByKey({ data, storeKey, applicationKey }) || Map()
  const uploadState = applicationData.get('state')
  const processing = uploadState === CONTENT_BULK_STATE_PROCESSING
  const error = applicationData.get('error')

  const [columnListVisible, showColumnList] = useState(false)
  const [redirectOnUnpublish, setRedirectOnUnpublish] = useState(false)
  const [pushToContentReference, setPushToContentReference] = useState(false)

  const onNoop = useCallback((e) => (
    e.preventDefault()
  ), [])

  const onOpenColumnList = useCallback(() => {
    showColumnList(true)
  }, [])

  const onCloseColumnList = useCallback(() => {
    showColumnList(false)
  }, [])

  const onImport = useCallback((e) => {
    e.preventDefault()
    persistData({
      options: {
        generatePath: true,
        redirectOnUnpublish,
        pushToContentReference,
      },
      record: applicationData.set('state', CONTENT_BULK_STATE_PROCESSING),
      processing: true,
      applicationKey,
      storeKey,
    })
  }, [
    processing,
    applicationData,
    redirectOnUnpublish,
    pushToContentReference,
  ])

  const onReset = useCallback((e) => {
    e.preventDefault()
    removeApplicationState({
      applicationKey,
      storeKey,
    })
  }, [])

  const onRedirectOnUnpublish = useCallback((e) => {
    setRedirectOnUnpublish(e.currentTarget.checked)
  }, [])

  const onPushToContentReference = ({ currentTarget: { checked } }) => {
    setPushToContentReference(checked)
  }

  const onDrop = useCallback(async (e) => {
    e.preventDefault()
    const file = _get(e, 'dataTransfer.files.0')
    const result = await parseCSV(file)
    setApplicationState({
      applicationData: result
        .set('state', CONTENT_BULK_STATE_UNPROCESSED),
      applicationKey,
      storeKey,
    })
  }, [])

  const onCreateCSV = useCallback(async (e) => {
    e.preventDefault()
    const uri = `data:text/csv;charset=utf-8,${COLUMNS.join(',')}`
    const encodedUri = encodeURI(uri)

    let link = document.createElement('a')
    link.setAttribute('href', encodedUri)
    link.setAttribute('download', 'bulk-upload-template.csv')
    link.addEventListener('click', () => {
      document.body.removeChild(link)
      link = null
    }, false)
    document.body.appendChild(link)
    link.click()
  }, [])

  return (
    <Container className="content-bulk">
      <h2>Welcome to the Bulk Update Tool</h2>
      <p>
        {'The Bulk Update tool allows you to update '}
        <span>Content</span>
        {' and '}
        <span>Content Relationships</span>
        {' by importing a CSV file of data points you wish to change.'}
        {' Drag a CSV file of valid columns into the box below. We will parse the file to determine if there are any immediate issues and provide you with the details.'}
        {' Once you are ready, "Import" the data using the actions below. Again, if we experience any issues while updating the Content, you\'ll be notified.'}
      </p>
      <div className="content-bulk__actions content-bulk__actions--help">
        <h3>Need some help?</h3>
        <p>{'View the list of columns or to create a template file using the actions below.'}</p>
        <Button disabled={processing} onClick={onOpenColumnList}>View Columns</Button>
        <Button disabled={processing} onClick={onCreateCSV}>Download Template</Button>
      </div>
      {
        applicationData.isEmpty()
        && <div className="content-bulk__actions content-bulk__actions--upload">
          <h3>Lets get started</h3>
          <p>Simply drag your files here.</p>
          <div
            className="content-bulk__drop"
            onDragOver={onNoop}
            onDrop={onDrop} />
        </div>
      }
      {
        !applicationData.isEmpty() && uploadState !== CONTENT_BULK_STATE_PROCESSED
        && <React.Fragment>
          <div className="content-bulk__actions content-bulk__actions--upload">
            <h3>Ready to upload?</h3>
            <p>{'Only valid rows will be uploaded to the server.'}</p>
            <Button disabled={processing} onClick={onImport}>Upload Content</Button>
            <Button disabled={processing} onClick={onReset}>Start Over</Button>
            {processing ? (
              <aside className="content-bulk__actions-aside">
                <h4 className="content-bulk__actions-aside-header">
                  {'We\'re updating content, please wait a moment...'}
                </h4>
              </aside>
            ) : (
              <>
                <aside className="content-bulk__actions-aside">
                  <h4 className="content-bulk__actions-aside-header">
                    Wait! Are you unpublishing content or providing redirects?
                  </h4>
                  <Checkbox
                    label="Yes, I am unpublishing content or providing redirects."
                    className="content-bulk__actions-aside-checkbox"
                    onChange={onRedirectOnUnpublish}
                    name="redirects"
                  />
                </aside>
                <aside className="content-bulk__actions-aside">
                  <h4 className="content-bulk__actions-aside-header">
                    Would you push new content reference value to the existing data?
                  </h4>
                  <Checkbox
                    label="Yes, push new content reference value to the existing data."
                    className="content-bulk__actions-aside-checkbox"
                    onChange={onPushToContentReference}
                    name="pushContentReference"
                  />
                </aside>
              </>
            )}
          </div>
          {error && <h4>{error}</h4>}
          <Table data={applicationData} uploadState={uploadState} />
        </React.Fragment>
      }
      {
        uploadState === CONTENT_BULK_STATE_PROCESSED
        && <React.Fragment>
          <div className="content-bulk__actions content-bulk__actions--upload">
            <h3>Content processed!</h3>
            <p>{'Below are details for both successfully and unsuccessfully saved content.'}</p>
            <Button disabled={processing} onClick={onReset}>Start Over</Button>
          </div>
          {error && <h4>{error}</h4>}
          <Table data={applicationData} uploadState={uploadState} />
        </React.Fragment>
      }
      {
        columnListVisible ? (
          <div className="content-bulk__column-list">
            <Container className="content-bulk__column-list-content">
              <Row>
                <h2>Column List</h2>
                <Button
                  className="content-bulk__column-list-close"
                  onClick={onCloseColumnList}>Close
                </Button>
              </Row>
              <Row>
                <Col sm="12" md="6">
                  <h3>Content</h3>
                  <ul className="content-bulk__column-list-columns">
                    {CONTENT_BULK_FIELDS.sort((a, b) => {
                      if (a > b) return 1
                      if (a < b) return -1
                      return 0
                    }).map((c) => (
                      <li key={c} className="content-bulk__column-list-item">
                        <h4>{c}</h4>
                        <dl className="content-bulk__column-list-details">
                          <div>
                            <dt>Required</dt>
                            <dd>
                              {
                                /^(contentId|contentType|language)$/.test(c)
                                  ? 'Yes' : 'No'
                              }
                            </dd>
                          </div>
                        </dl>
                      </li>
                    ))}
                  </ul>
                </Col>
                <Col sm="12" md="6">
                  <h3>Content Relationships</h3>
                  <ul className="content-bulk__column-list-columns">
                    {CONTENT_REFERENCE_TYPES.sort((a, b) => {
                      if (a > b) return 1
                      if (a < b) return -1
                      return 0
                    }).map((c) => (
                      <li className="content-bulk__column-list-item" key={c}>
                        <h4>{c}</h4>
                        <dl className="content-bulk__column-list-details">
                          <div>
                            <dt>Required</dt>
                            <dd>No</dd>
                          </div>
                          <div>
                            <dt>Value</dt>
                            <dd>One or more contentIds separated by a comma</dd>
                          </div>
                        </dl>
                      </li>
                    ))}
                  </ul>
                </Col>
              </Row>
            </Container>
          </div>
        ) : null
      }
    </Container>
  )
}

ContentBulk.propTypes = {
  removeApplicationState: PropTypes.func.isRequired,
  storeKeyContentPath: PropTypes.string.isRequired,
  setApplicationState: PropTypes.func.isRequired,
  location: ImmutablePropTypes.map.isRequired,
  data: ImmutablePropTypes.map.isRequired,
  persistData: PropTypes.func.isRequired,
  setDataList: PropTypes.func.isRequired,
  storeKey: PropTypes.string.isRequired,
  history: PropTypes.object.isRequired,
}

ContentBulk.defaultProps = {
  applicationKey: CONTENT_BULK_APPLICATION_KEY,
  storeKeyContentPath: CONTENT_PATH_STORE_KEY,
  storeKey: CONTENT_STORE_KEY,
}

export default compose(
  connectRedux(
    (state) => _pick(state, ['data', 'location']),
    (dispatch) => bindActionCreators({
      removeApplicationState: deleteDataApplicationByKeyAction,
      setApplicationState: setDataApplicationByKeyAction,
      persistData: persistDataAction,
      setDataList: setDataListAction,
    }, dispatch),
  ),
  connectHistory(),
)(ContentBulk)
